import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "350px",
  },
}));

interface TopContact {
  contactId: number;
  contactName: string;
  count: number;
}

interface TopContactsChartProps {
  data: TopContact[];
  metric: "Tickets" | "Mensagens";
}

const COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#FFA07A",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E2",
  "#F8B88B",
  "#ABEBC6",
];

const TopContactsChart: React.FC<TopContactsChartProps> = ({
  data,
  metric,
}) => {
  const classes = useStyles();

  // Preparar dados para gráfico de barras horizontais (inverter eixos)
  // Backend retorna dados ordenados por count DESC — primeiro item do array
  // aparece no topo do recharts vertical, portanto NÃO invertemos a ordem.
  const chartData = (data || [])
    .slice(0, 10)
    .map((item, index) => {
      const hasName =
        item.contactName && item.contactName.trim().length > 0;
      const fullName = hasName ? item.contactName : "Contato sem nome";
      // Contatos reais: truncar em 20 chars para não quebrar layout.
      // Fallback "Contato sem nome" (16 chars): manter inteiro, sem truncar.
      const axisName = hasName
        ? fullName.substring(0, 20)
        : fullName;

      return {
        name: axisName,
        value: item.count,
        fullName,
        color: COLORS[index % COLORS.length],
      };
    });

  return (
    <Box className={classes.container}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis
            dataKey="name"
            type="category"
            tick={{ fontSize: 11 }}
            width={145}
            label={{
              value: "Contatos",
              angle: -90,
              position: "insideLeft",
            }}
          />
          <Tooltip
            cursor={{ fill: "rgba(200, 200, 200, 0.1)" }}
            contentStyle={{
              backgroundColor: "#f9f9f9",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
            formatter={(value, name, props: any) => [
              `${value} ${metric.toLowerCase()}`,
              props.payload.fullName,
            ]}
            labelFormatter={(label) => `${label}`}
          />
          <Bar dataKey="value" fill="#8884d8" radius={[0, 8, 8, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TopContactsChart;
